/**
 * /execution/qa-callbacks — read-only inbound queue.
 * Source: AppDataContext.callbacks (canonical, append-only).
 *
 * Lifecycle honesty pass: rows are grouped by Fresh / Aging / Stale
 * buckets, and each row carries an optional anomaly tag explaining
 * why it is worth a second look. Pure presentation — no writers.
 */
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Link } from "react-router-dom";
import { ExternalLink, LifeBuoy } from "lucide-react";
import { openResolutionDrawer } from "@/data/execution/resolutionDrawerBus";
import { UnifiedResolutionDrawer } from "@/components/execution/UnifiedResolutionDrawer";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { canAccessExecutionDomain } from "@/data/executionDomain";
import {
  classifyCallbackLifecycle,
  classifyCallbackAnomaly,
  CALLBACK_BUCKET_ORDER,
  type CallbackLifecycleBucket,
} from "@/data/execution/lifecycleClassifiers";

type Range = "24h" | "7d" | "30d" | "all";
const DAY = 86_400_000;
const WIN: Record<Range, number | null> = { "24h": DAY, "7d": 7 * DAY, "30d": 30 * DAY, all: null };

const BUCKET_HEADER: Record<CallbackLifecycleBucket, string> = {
  fresh: "Fresh — last 24h",
  aging: "Aging — 1 to 7 days",
  stale: "Stale — over 7 days",
};

const ANOMALY_TONE_CLASS: Record<"warning" | "destructive" | "muted", string> = {
  warning: "border-warning text-warning",
  destructive: "border-destructive text-destructive",
  muted: "border-muted-foreground text-muted-foreground",
};

export default function ExecutionQACallbacksPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { role } = useRole();
  const { contractors, callbacks } = useAppData();
  const [range, setRange] = useState<Range>("7d");
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const cutoff = WIN[range];
    const now = Date.now();
    const ql = q.trim().toLowerCase();
    return callbacks
      .filter((c) => (cutoff === null ? true : now - new Date(c.filedAt).getTime() <= cutoff))
      .filter((c) =>
        ql.length === 0 ||
        c.jobId.toLowerCase().includes(ql) ||
        (c.installerContractorIdAtCallback ?? "").toLowerCase().includes(ql) ||
        c.type.toLowerCase().includes(ql),
      )
      .sort((a, b) => +new Date(b.filedAt) - +new Date(a.filedAt))
      .slice(0, 200)
      .map((c) => ({
        callback: c,
        lifecycle: classifyCallbackLifecycle(c),
        anomaly: classifyCallbackAnomaly(c),
      }));
  }, [callbacks, range, q]);

  const grouped = useMemo(() => {
    const map = new Map<CallbackLifecycleBucket, typeof rows>();
    CALLBACK_BUCKET_ORDER.forEach((b) => map.set(b, []));
    rows.forEach((r) => map.get(r.lifecycle.bucket)!.push(r));
    return map;
  }, [rows]);

  const contractorById = useMemo(() => {
    const m = new Map<string, string>();
    contractors.forEach((c) => m.set(c.id, c.companyName));
    return m;
  }, [contractors]);

  if (!canAccessExecutionDomain(role)) return null;

  const content = (
    <>
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <Input placeholder="Filter by job, contractor, or type…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        <Badge variant="outline" className="text-[10px]">{rows.length} shown</Badge>
        <span className="text-[11px] text-muted-foreground">
          Grouped by age. Tags surface owner-attributed callbacks (excluded from executor scorecards), unassigned snapshots, and unack'd high-severity auto-detections.
        </span>
      </div>

      {rows.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No callbacks in this window. The queue stays clean when execution holds.
        </Card>
      ) : (
        <div className="space-y-4">
          {CALLBACK_BUCKET_ORDER.map((bucket) => {
            const bucketRows = grouped.get(bucket)!;
            if (bucketRows.length === 0) return null;
            return (
              <div key={bucket}>
                <div className="sticky top-0 z-10 mb-2 flex items-center justify-between bg-background/95 backdrop-blur py-1">
                  <h2 className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                    {BUCKET_HEADER[bucket]}
                  </h2>
                  <Badge variant="outline" className="text-[10px]">{bucketRows.length}</Badge>
                </div>
                <Card className="p-0 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Filed</TableHead>
                        <TableHead>Job</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Installer</TableHead>
                        <TableHead>Why flagged</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right"> </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bucketRows.map(({ callback: c, anomaly }) => (
                        <TableRow key={c.id}>
                          <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">
                            {format(new Date(c.filedAt), "MMM d, h:mm a")}
                          </TableCell>
                          <TableCell className="font-mono text-[11px]">{c.jobId}</TableCell>
                          <TableCell className="text-[12px]">{c.type}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${
                                c.severity === "high" ? "border-destructive text-destructive" :
                                c.severity === "medium" ? "border-warning text-warning" :
                                "border-muted-foreground text-muted-foreground"
                              }`}
                            >
                              {c.severity}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-[11px] text-muted-foreground">{c.source}</TableCell>
                          <TableCell className="text-[11px]">
                            {c.installerContractorIdAtCallback
                              ? contractorById.get(c.installerContractorIdAtCallback) ?? c.installerContractorIdAtCallback
                              : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell>
                            {anomaly ? (
                              <Badge
                                variant="outline"
                                className={`text-[10px] ${ANOMALY_TONE_CLASS[anomaly.tone]}`}
                                title={anomaly.detail}
                              >
                                {anomaly.label}
                              </Badge>
                            ) : (
                              <span className="text-[11px] text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-[11px] max-w-[280px] truncate" title={c.description}>{c.description}</TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`qa-callback-triage-${c.id}`}
                                onClick={() =>
                                  openResolutionDrawer({
                                    jobId: c.jobId,
                                    source: "qa_callback_row",
                                    eventContext: {
                                      kind: `callback_${c.type}`,
                                      at: c.filedAt,
                                      headline: `Callback — ${c.type.replace(/_/g, " ")}`,
                                      detail: c.description,
                                    },
                                  })
                                }
                              >
                                <LifeBuoy className="h-3 w-3" />
                              </Button>
                              <Button asChild size="sm" variant="ghost">
                                <Link to={`/execution/timeline?job=${c.jobId}`}>
                                  <ExternalLink className="h-3 w-3" />
                                </Link>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  if (embedded) return content;

  return (
    <>
      <CockpitPage
        
        title="QA & Callbacks"
        maxWidth="7xl"
        controls={
          <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
            {(["24h", "7d", "30d", "all"] as Range[]).map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-2.5 py-1 text-[11px] rounded ${
                  range === r
                    ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        }
      >
        {content}
      </CockpitPage>
      <UnifiedResolutionDrawer />
    </>
  );
}
